import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiChevronLeft, FiChevronRight, FiPlay, FiBook } = FiIcons;

const TutorialModal = ({ isOpen, onClose, onStartTutorial }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const tutorialSlides = [
    {
      title: "Welcome to Chess!",
      content: "Learn the basics of chess with our interactive tutorial system.",
      image: "♔♕♖♗♘♙",
      description: "Chess is a strategic board game played between two players on an 8x8 board with 64 squares."
    },
    {
      title: "The Chess Board",
      content: "The board has 64 squares arranged in 8 rows and 8 columns.",
      image: "🏁",
      description: "Light and dark squares alternate. The bottom-right square should always be light-colored."
    },
    {
      title: "Chess Pieces",
      content: "Each player starts with 16 pieces: 1 King, 1 Queen, 2 Rooks, 2 Bishops, 2 Knights, and 8 Pawns.",
      image: "♔♕♖♗♘♙",
      description: "Every piece has unique movement patterns and abilities."
    },
    {
      title: "Game Objective",
      content: "The goal is to checkmate your opponent's king.",
      image: "🎯",
      description: "Checkmate occurs when the king is in check and cannot escape capture."
    },
    {
      title: "How to Play",
      content: "Players take turns moving one piece at a time.",
      image: "⏱️",
      description: "White always moves first. You have 2 minutes per move in this game."
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % tutorialSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + tutorialSlides.length) % tutorialSlides.length);
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentSlide(0);
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl shadow-2xl border border-white/20 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiBook} className="text-3xl text-blue-400" />
                <h2 className="text-2xl font-bold text-white">Chess Tutorial</h2>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <SafeIcon icon={FiX} className="text-2xl" />
              </motion.button>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-400 mb-2">
                <span>Step {currentSlide + 1} of {tutorialSlides.length}</span>
                <span>{Math.round(((currentSlide + 1) / tutorialSlides.length) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <motion.div
                  className="bg-blue-500 h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentSlide + 1) / tutorialSlides.length) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>

            {/* Slide Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                className="text-center mb-8"
              >
                <div className="text-6xl mb-4">
                  {tutorialSlides[currentSlide].image}
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  {tutorialSlides[currentSlide].title}
                </h3>
                <p className="text-lg text-blue-300 mb-4">
                  {tutorialSlides[currentSlide].content}
                </p>
                <p className="text-gray-300">
                  {tutorialSlides[currentSlide].description}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={prevSlide}
                disabled={currentSlide === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
                  currentSlide === 0
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-700 text-white hover:bg-gray-600'
                }`}
              >
                <SafeIcon icon={FiChevronLeft} />
                Previous
              </motion.button>

              <div className="flex gap-2">
                {tutorialSlides.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentSlide ? 'bg-blue-500' : 'bg-gray-600'
                    }`}
                  />
                ))}
              </div>

              {currentSlide === tutorialSlides.length - 1 ? (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onStartTutorial}
                  className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
                >
                  <SafeIcon icon={FiPlay} />
                  Start Interactive Tutorial
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={nextSlide}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                >
                  Next
                  <SafeIcon icon={FiChevronRight} />
                </motion.button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TutorialModal;